import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Bell, Mail, Calendar, Flame, Heart, Check } from 'lucide-react';
import { useState } from 'react';

interface NotificationPreferences {
  id: string;
  memberId: string;
  yahrzeitReminders: boolean;
  yahrzeitReminderDays: number[];
  eventReminders: boolean;
  eventReminderHours: number;
  membershipReminders: boolean;
  newsletterEnabled: boolean;
  donationReceipts: boolean;
}

interface NotificationLog {
  id: string;
  type: string;
  subject: string;
  status: string;
  sentAt: string;
}

const REMINDER_DAY_OPTIONS = [
  { value: 30, label: '30 days before' },
  { value: 14, label: '2 weeks before' },
  { value: 7, label: '1 week before' },
  { value: 3, label: '3 days before' },
  { value: 1, label: '1 day before' },
  { value: 0, label: 'Day of' },
];

const EVENT_REMINDER_OPTIONS = [
  { value: 48, label: '2 days before' },
  { value: 24, label: '1 day before' },
  { value: 12, label: '12 hours before' },
  { value: 6, label: '6 hours before' },
  { value: 2, label: '2 hours before' },
  { value: 1, label: '1 hour before' },
];

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const response = await api.get<NotificationPreferences>('/notifications/preferences');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: history } = useQuery({
    queryKey: ['notification-history'],
    queryFn: async () => {
      const response = await api.get<NotificationLog[]>('/notifications/history?limit=10');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const response = await api.patch<NotificationPreferences>('/notifications/preferences', updates);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleToggle = (key: keyof NotificationPreferences, value: boolean) => {
    updateMutation.mutate({ [key]: value });
  };

  const handleYahrzeitDaysChange = (day: number, checked: boolean) => {
    const currentDays = preferences?.yahrzeitReminderDays || [7, 1];
    const newDays = checked
      ? [...currentDays, day].sort((a, b) => b - a)
      : currentDays.filter((d) => d !== day);
    updateMutation.mutate({ yahrzeitReminderDays: newDays });
  };

  const handleEventReminderChange = (hours: number) => {
    updateMutation.mutate({ eventReminderHours: hours });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
        <div className="animate-pulse space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage how and when you receive notifications from the synagogue.
          </p>
        </div>
        {saveSuccess && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
            <Check className="h-4 w-4" />
            <span className="text-sm">Settings saved</span>
          </div>
        )}
      </div>

      {/* Yahrzeit Reminders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Flame className="h-5 w-5 text-amber-600" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Yahrzeit Reminders</h2>
                <p className="text-sm text-gray-500">
                  Receive reminders before yahrzeit observances
                </p>
              </div>
              <ToggleSwitch
                enabled={preferences?.yahrzeitReminders ?? true}
                onChange={(v) => handleToggle('yahrzeitReminders', v)}
              />
            </div>

            {preferences?.yahrzeitReminders && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-700 mb-3">Remind me:</p>
                <div className="flex flex-wrap gap-2">
                  {REMINDER_DAY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={preferences?.yahrzeitReminderDays?.includes(option.value) ?? false}
                        onChange={(e) => handleYahrzeitDaysChange(option.value, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Reminders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Event Reminders</h2>
                <p className="text-sm text-gray-500">
                  Receive reminders for events you're registered for
                </p>
              </div>
              <ToggleSwitch
                enabled={preferences?.eventReminders ?? true}
                onChange={(v) => handleToggle('eventReminders', v)}
              />
            </div>

            {preferences?.eventReminders && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-700 mb-3">Remind me:</p>
                <select
                  value={preferences?.eventReminderHours ?? 24}
                  onChange={(e) => handleEventReminderChange(parseInt(e.target.value, 10))}
                  className="block w-full max-w-xs rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                >
                  {EVENT_REMINDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Other Notifications */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">Other Notifications</h2>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Bell className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Membership Reminders</p>
              <p className="text-xs text-gray-500">Renewal notices and membership updates</p>
            </div>
          </div>
          <ToggleSwitch
            enabled={preferences?.membershipReminders ?? true}
            onChange={(v) => handleToggle('membershipReminders', v)}
          />
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
              <Mail className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Newsletter</p>
              <p className="text-xs text-gray-500">Weekly updates and announcements</p>
            </div>
          </div>
          <ToggleSwitch
            enabled={preferences?.newsletterEnabled ?? true}
            onChange={(v) => handleToggle('newsletterEnabled', v)}
          />
        </div>

        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-pink-100 flex items-center justify-center">
              <Heart className="h-4 w-4 text-pink-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Donation Receipts</p>
              <p className="text-xs text-gray-500">Email receipts for donations</p>
            </div>
          </div>
          <ToggleSwitch
            enabled={preferences?.donationReceipts ?? true}
            onChange={(v) => handleToggle('donationReceipts', v)}
          />
        </div>
      </div>

      {/* Recent Notifications */}
      {history && history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Notifications</h2>
          <div className="space-y-3">
            {history.map((notification) => (
              <div
                key={notification.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-gray-50"
              >
                <NotificationIcon type={notification.type} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {notification.subject}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(notification.sentAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                <StatusBadge status={notification.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        enabled ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function NotificationIcon({ type }: { type: string }) {
  if (type.includes('yahrzeit')) {
    return (
      <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Flame className="h-4 w-4 text-amber-600" />
      </div>
    );
  }
  if (type.includes('event')) {
    return (
      <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
        <Calendar className="h-4 w-4 text-blue-600" />
      </div>
    );
  }
  return (
    <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
      <Bell className="h-4 w-4 text-gray-600" />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        Sent
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
      {status}
    </span>
  );
}
